import type { Family } from './types.ts';

export type FieldKind =
  | 'text'
  | 'longtext'
  | 'number'
  | 'doc'
  | 'duration'
  | 'options'
  | 'range'
  | 'variable'
  | 'connection'
  | 'recipients'
  | 'url'
  | 'folder'
  | 'workflow'
  | 'choice'
  | 'map'
  | 'loop';

export interface FieldSpec {
  name: string;
  kind: FieldKind;
  label: string;
  required: boolean;
  editable: boolean;
  templated: boolean;
  choices?: readonly string[];
  mediaKind?: 'image' | 'video' | 'document';
}

export type AnswerMode = 'yes_no' | 'options' | 'number' | 'text' | 'none';
export type BranchMode = 'answers' | 'conditions' | 'none';

export interface StepTypeSpec {
  type: string;
  family: Family;
  title: string;
  icon: string;
  example: string;
  answers: AnswerMode;
  branches: BranchMode;
  container: boolean;
  capability?: string;
  fields: readonly FieldSpec[];
}

type FieldOptions = Partial<Pick<FieldSpec, 'required' | 'editable' | 'templated' | 'choices' | 'mediaKind'>>;

function field(name: string, kind: FieldKind, label: string, options: FieldOptions = {}): FieldSpec {
  return {
    name,
    kind,
    label,
    required: options.required ?? false,
    editable: options.editable ?? true,
    templated: options.templated ?? false,
    ...(options.choices ? { choices: options.choices } : {}),
    ...(options.mediaKind ? { mediaKind: options.mediaKind } : {}),
  };
}

const say = (required: boolean) => field('say', 'text', 'Spoken and shown text', { required, templated: true });
const caption = field('caption', 'text', 'Caption', { templated: true });
const store = field('store', 'variable', 'Save the answer as', { editable: false });
const connection = (required: boolean) =>
  field('connection', 'connection', 'Connection', { required, editable: false });

export const STEP_TYPE_LIST: readonly StepTypeSpec[] = [
  {
    type: 'ask.yes_no',
    family: 'ask',
    title: 'Yes or no',
    icon: 'solar:question-circle-linear',
    example: 'Ask if they are wearing gloves, and stop if not.',
    answers: 'yes_no',
    branches: 'answers',
    container: false,
    fields: [say(true), store],
  },
  {
    type: 'ask.choice',
    family: 'ask',
    title: 'Pick from a list',
    icon: 'solar:list-check-linear',
    example: 'Ask which pump model it is: X200, X300 or other.',
    answers: 'options',
    branches: 'answers',
    container: false,
    fields: [say(true), field('options', 'options', 'Options', { required: true }), store],
  },
  {
    type: 'ask.number',
    family: 'ask',
    title: 'Number',
    icon: 'solar:calculator-linear',
    example: 'Ask for the pressure in bar, between 0 and 10.',
    answers: 'number',
    branches: 'conditions',
    container: false,
    fields: [say(true), field('range', 'range', 'Allowed range'), field('unit', 'text', 'Unit'), store],
  },
  {
    type: 'ask.free',
    family: 'ask',
    title: 'Free answer',
    icon: 'solar:microphone-linear',
    example: 'Ask them to describe any damage they see.',
    answers: 'text',
    branches: 'conditions',
    container: false,
    fields: [say(true), store],
  },
  {
    type: 'show.image',
    family: 'show',
    title: 'Show an image',
    icon: 'solar:gallery-linear',
    example: 'Show the photo of the valve panel.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [field('media', 'doc', 'Image', { required: true, mediaKind: 'image' }), caption, say(false)],
  },
  {
    type: 'show.video',
    family: 'show',
    title: 'Show a video',
    icon: 'solar:videocamera-record-linear',
    example: 'Play the lockout procedure video.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [field('media', 'doc', 'Video', { required: true, mediaKind: 'video' }), caption, say(false)],
  },
  {
    type: 'show.document_page',
    family: 'show',
    title: 'Show a document page',
    icon: 'solar:document-text-linear',
    example: 'Show page 12 of the X200 manual.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [
      field('media', 'doc', 'Document', { required: true, mediaKind: 'document' }),
      field('page', 'number', 'Page'),
      caption,
      say(false),
    ],
  },
  {
    type: 'show.card',
    family: 'show',
    title: 'Instruction card',
    icon: 'solar:notes-linear',
    example: 'Tell them to close valve 3 before continuing.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [say(true), field('body', 'longtext', 'More detail', { templated: true })],
  },
  {
    type: 'show.web',
    family: 'show',
    title: 'Show a web page',
    icon: 'solar:global-linear',
    example: 'Show the YouTube video on seal replacement.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [field('url', 'url', 'Address', { required: true, templated: true }), caption, say(false)],
  },
  {
    type: 'act.email',
    family: 'act',
    title: 'Send an email',
    icon: 'solar:letter-linear',
    example: 'Email QA the pump number and pressure when it is above 4 bar.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    capability: 'send_email',
    fields: [
      connection(true),
      field('to', 'recipients', 'To', { required: true, templated: true }),
      field('subject', 'text', 'Subject', { required: true, templated: true }),
      field('body', 'longtext', 'Message', { templated: true }),
    ],
  },
  {
    type: 'act.teams_message',
    family: 'act',
    title: 'Send a Teams message',
    icon: 'solar:chat-round-line-linear',
    example: 'Message the shift lead on Teams when a leak is found.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    capability: 'send_teams_message',
    fields: [
      connection(true),
      field('to', 'recipients', 'To', { required: true, templated: true }),
      field('body', 'longtext', 'Message', { required: true, templated: true }),
    ],
  },
  {
    type: 'act.calendar_invite',
    family: 'act',
    title: 'Send a calendar invite',
    icon: 'solar:calendar-add-linear',
    example: 'Book a 30 minute follow-up with the shift lead tomorrow at 9.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    capability: 'create_calendar_event',
    fields: [
      connection(true),
      field('to', 'recipients', 'Invite', { required: true, templated: true }),
      field('subject', 'text', 'Subject', { required: true, templated: true }),
      field('start', 'text', 'Start', { required: true, templated: true }),
      field('duration', 'duration', 'Length', { required: true, templated: true }),
      field('body', 'longtext', 'Details', { templated: true }),
    ],
  },
  {
    type: 'act.hubspot',
    family: 'act',
    title: 'Update HubSpot',
    icon: 'solar:users-group-rounded-linear',
    example: 'Log a note on the customer record in HubSpot with the result.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    capability: 'hubspot',
    fields: [
      connection(true),
      field('operation', 'choice', 'Operation', {
        required: true,
        editable: false,
        choices: ['create_record', 'update_record', 'log_note'],
      }),
      field('object', 'text', 'Record type', { required: true, editable: false }),
      field('fields', 'map', 'Fields', { templated: true }),
    ],
  },
  {
    type: 'act.http',
    family: 'act',
    title: 'Call an API',
    icon: 'solar:server-linear',
    example: 'Send the inspection result to our maintenance system API.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [
      connection(false),
      field('method', 'choice', 'Method', {
        required: true,
        editable: false,
        choices: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
      }),
      field('url', 'url', 'Address', { required: true, templated: true }),
      field('headers', 'map', 'Headers', { editable: false }),
      field('body', 'longtext', 'Body', { templated: true }),
      store,
    ],
  },
  {
    type: 'act.report_pdf',
    family: 'act',
    title: 'Create a PDF report',
    icon: 'solar:file-text-linear',
    example: 'Create a PDF report of the run in the outputs folder.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [
      field('into', 'folder', 'Folder', { required: true, editable: false }),
      field('report_title', 'text', 'Report title', { templated: true }),
    ],
  },
  {
    type: 'act.report_xlsx',
    family: 'act',
    title: 'Add to the Excel log',
    icon: 'solar:document-add-linear',
    example: 'Add a row with every answer to the inspection spreadsheet.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [field('into', 'folder', 'Folder', { required: true, editable: false })],
  },
  {
    type: 'flow.repeat',
    family: 'flow',
    title: 'Repeat',
    icon: 'solar:repeat-linear',
    example: 'Repeat the pressure check for each of the 6 pumps.',
    answers: 'none',
    branches: 'none',
    container: true,
    fields: [field('for_each', 'loop', 'Repeat', { required: true, editable: false })],
  },
  {
    type: 'flow.wait',
    family: 'flow',
    title: 'Wait',
    icon: 'solar:hourglass-linear',
    example: 'Wait 10 minutes for the sealant to cure.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [field('duration', 'duration', 'Duration', { required: true }), say(false)],
  },
  {
    type: 'flow.end',
    family: 'flow',
    title: 'End',
    icon: 'solar:flag-linear',
    example: 'Stop the inspection as unsafe.',
    answers: 'none',
    branches: 'none',
    container: false,
    fields: [field('status', 'text', 'Result', { required: true, editable: false }), say(false)],
  },
  {
    type: 'flow.start_workflow',
    family: 'flow',
    title: 'Start another workflow',
    icon: 'solar:square-share-line-linear',
    example: 'If a leak is found, start the incident report workflow.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [
      field('skill', 'workflow', 'Workflow', { required: true, editable: false }),
      field('assign_to', 'choice', 'Who runs it', { editable: false, choices: ['same_worker', 'supervisor'] }),
    ],
  },
  {
    type: 'escalate.call',
    family: 'escalate',
    title: 'Call an expert',
    icon: 'solar:phone-calling-linear',
    example: 'Call the remote expert on Teams if the seal is damaged.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [field('contact', 'text', 'Contact', { required: true }), say(false)],
  },
  {
    type: 'escalate.signoff',
    family: 'escalate',
    title: 'Supervisor sign off',
    icon: 'solar:shield-check-linear',
    example: 'Have the supervisor approve before restarting the pump.',
    answers: 'none',
    branches: 'conditions',
    container: false,
    fields: [field('approver', 'text', 'Approver', { required: true }), say(false)],
  },
];

export const STEP_TYPES: Readonly<Record<string, StepTypeSpec>> = Object.fromEntries(
  STEP_TYPE_LIST.map((spec) => [spec.type, spec])
);

export function getStepType(type: string): StepTypeSpec | undefined {
  return Object.prototype.hasOwnProperty.call(STEP_TYPES, type) ? STEP_TYPES[type] : undefined;
}
